// Save a search: name it, and choose whether new matches raise alerts. Saved searches return exactly
// what matches; saved as a feed instead, the search becomes the feed's and the feed learns what you like.
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, errorText } from "../api/client";
import { FREQ, type Frequency, type Sort } from "../api/search";
import { useUI } from "../app/context";
import { keys, useSession } from "../app/queries";
import { Modal } from "./Modal";

export function SaveSearchDialog({
  open,
  onClose,
  query,
  sort,
  frequency,
  name,
}: {
  open: boolean;
  onClose: () => void;
  query: string;
  sort: Sort;
  frequency: Frequency;
  name: string;
}) {
  return (
    <Modal open={open} onClose={onClose} id="save-dialog">
      <SaveForm onClose={onClose} query={query} sort={sort} frequency={frequency} name={name} />
    </Modal>
  );
}

function SaveForm({ onClose, query, sort, frequency, name }: { onClose: () => void; query: string; sort: Sort; frequency: Frequency; name: string }) {
  const [title, setTitle] = useState(name);
  const [freq, setFreq] = useState<Frequency>(frequency);
  const [as, setAs] = useState<"search" | "feed">("search");
  const emailAvailable = !!useSession().email_alerts;
  const client = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useUI();
  const save = async () => {
    try {
      if (as === "feed") {
        const r = await api<{ id: number }>("/api/feeds", { method: "POST", body: { name: title.trim() || "My feed", query } });
        if (freq !== "off") await api(`/api/feeds/${r.id}/alerts`, { method: "PUT", body: { frequency: freq } });
        await client.invalidateQueries({ queryKey: keys.feeds });
        onClose();
        navigate(`/feeds/${r.id}?view=all`);
        showToast("Feed created. Mark records Pursue or Watch to teach it; Edit feed narrows it.");
        return;
      }
      const r = await api<{ id: number }>("/api/searches", {
        method: "POST",
        body: { name: title.trim() || "My search", query, sort, frequency: freq },
      });
      await client.invalidateQueries({ queryKey: keys.searches });
      onClose();
      navigate(`/saved/${r.id}`);
      showToast(freq === "off" ? "Search saved." : "Search saved. New matches will show up in Alerts.");
    } catch (err) {
      showToast(errorText(err));
    }
  };
  return (
    <form
      method="dialog"
      onSubmit={(e) => {
        e.preventDefault();
        const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
        if (submitter?.value === "ok") void save();
        else onClose();
      }}
    >
      <div className="dlg">
        <h2>Save search</h2>
        <p>Come back to it from the sidebar. Saved searches return exactly what matches; feeds learn what you like.</p>
        <div className="field">
          <span>Save as</span>
          <div className="opts" id="ss-as">
            {(
              [
                ["search", "A saved search", "Returns exactly what matches."],
                ["feed", "A feed", "Holds what matches, learns from what you mark, and can be shared and narrowed further."],
              ] as const
            ).map(([v, l, sub]) => (
              <label key={v}>
                <input type="radio" name="ssas" value={v} checked={as === v} onChange={() => setAs(v)} />
                <span>
                  {l}
                  <small>{sub}</small>
                </span>
              </label>
            ))}
          </div>
        </div>
        <label className="field">
          <span>Name</span>
          <input type="text" id="ss-name" maxLength={120} required value={title} autoFocus onFocus={(e) => e.target.select()} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <div className="field">
          <span>Query</span>
          <code id="ss-query" className="query-box">
            {query}
          </code>
        </div>
        <div className="field">
          <span>Alert me about new matches</span>
          <div className="opts" id="ss-freq">
            {FREQ.map(([v, l, s]) => (
              <label key={v}>
                <input type="radio" name="ssf" value={v} checked={freq === v} onChange={() => setFreq(v)} />
                <span>
                  {l}
                  <small>{s}</small>
                </span>
              </label>
            ))}
          </div>
          <small id="ss-email-note" hidden={emailAvailable}>
            Email isn't set up on this server, so alerts appear under Alerts in GovChew.
          </small>
        </div>
      </div>
      <div className="foot">
        <button className="btn" value="cancel" formNoValidate>
          Cancel
        </button>
        <button className="btn primary" value="ok">
          {as === "feed" ? "Create feed" : "Save search"}
        </button>
      </div>
    </form>
  );
}
